import { incomeVerificationDashboardHttp } from './incomeVerificationDashboardClient';

const BASE = '/api/income-verification/v1/admin/dashboard';

export type IncomeGateKey = 'RECURRENCE' | 'STABILITY' | 'ARBITRATION';
export type IncomeGateOutcome = 'PASS' | 'FAIL' | 'REVIEW' | 'NOT_RUN';

export type IncomeDashboardSummary = {
  totals: Record<string, number>;
  outcomeTiles: Array<{
    id: string;
    finalStatus: string | null;
    title: string;
    subtitle: string;
    presentation: 'success' | 'error' | 'warning' | 'neutral';
    count: number;
  }>;
  listControls: {
    sortOptions: Array<{ value: string; label: string; description: string }>;
    outcomeQuickFilters: Array<{ finalStatus: string | null; label: string }>;
  };
  gateFlowSteps: Array<{
    gateKey: string;
    shortCode: string;
    title: string;
    whatHappens: string;
    outcomesExplained: string;
  }>;
  pipelineStatusLegend: Record<string, { label: string; operatorMeaning: string }>;
  finalDecisionLegend: Record<string, string>;
};

export type IncomeDashboardListItem = {
  id: string;
  applicationId: string;
  sessionId: string;
  ccid: string | null;
  finalStatus: string;
  confidenceScore: number | null;
  failReasons: string[];
  attemptNumber: number | null;
  verifiedAt: string | null;
  metrics: {
    normalizedMonthlyIncome: number | null;
    stabilityScore: number | null;
    volatilityIndex: number | null;
    recurrenceConfidence: number | null;
    frequencyClassification: string | null;
    primaryPayor: string | null;
    incomeClass: string | null;
    incomeType: string | null;
    depositsUsed: number;
    depositsExcluded: number;
  } | null;
  gates: Record<IncomeGateKey, IncomeGateOutcome>;
  gateMessages: Record<IncomeGateKey, string | null>;
};

export type IncomeDashboardListResponse = {
  total: number;
  limit: number;
  offset: number;
  items: IncomeDashboardListItem[];
};

export type IncomeDashboardIncomeStream = {
  payor: string;
  payorKey: string;
  monthlyAmount: number;
  frequency: string | null;
  depositsUsed: number;
  streamVerdict: string | null;
  isPrimary: boolean;
  usedForIncomeDetermination: boolean;
};

export type IncomeDashboardDetail = {
  id: string;
  applicationId: string;
  sessionId: string;
  ccid: string | null;
  verdict: string | null;
  finalStatus: string;
  confidenceScore: number | null;
  failReasons: string[];
  attemptNumber: number | null;
  verifiedAt: string | null;
  outcomes: {
    recurrenceOutcome: string | null;
    stabilityOutcome: string | null;
    arbitrationOutcome: string | null;
  };
  gates: Record<IncomeGateKey, IncomeGateOutcome>;
  gateMessages: Record<IncomeGateKey, string | null>;
  metrics: IncomeDashboardListItem['metrics'];
  session: {
    id: string;
    ccid: string | null;
    status: string;
    ibvStatus: string;
    channel: string;
    providerConfigId: string | null;
    createdAt: string | null;
    expiresAt: string | null;
  } | null;
  application: {
    id: string;
    status: string;
    productCode: string | null;
    fundedAt: string | null;
  } | null;
  auditTrail: Array<{
    id: string;
    event: string;
    riskContribution: number;
    payload: Record<string, unknown> | null;
    createdAt: string | null;
  }>;
  bankTransactions: Array<{
    id: string;
    postedAt: string;
    description: string;
    direction: 'CREDIT' | 'DEBIT' | 'NEUTRAL';
    debit: number | null;
    credit: number | null;
    amount: number | null;
    accountExternalId: string | null;
    accountType: string | null;
    institutionCode: string | null;
    institutionName: string | null;
    batchFetchStatus: string | null;
    batchCreatedAt: string | null;
    externalTransactionId: string | null;
    isInvalid: boolean;
    invalidReason: string | null;
    summaryLine: string;
    rawDataHints: string | null;
    /**
     * Present only when ATA matched this row back to a stored adverse group
     * (currently gambling only - see trimSignalsForStorage). null does not mean
     * "checked and clean", it means no adverse signal was recorded here.
     */
    adverseCategory: string | null;
    adverseMerchant: string | null;
  }>;
  incomeDeposits: Array<{
    id: string;
    sourceTransactionId: string;
    depositDate: string;
    payorDisplay: string | null;
    /** Verbatim bank Description when it differs from payorDisplay. */
    payorRaw?: string | null;
    payorNormalized: string;
    payorType: string;
    amount: number | null;
    incomeClass: string | null;
    frequencyDetected: string | null;
    isEligible: boolean;
    /** When true, deposit is in BankIncomeAudit.outlierDetails.excluded (shown in Excluded tab only). */
    excludedByAudit?: boolean;
    exclusionReason: string | null;
    isInvalid: boolean;
    invalidReason: string | null;
    usedInModel: boolean;
    /** Normalized payor key when deposit fed an included income stream (admin multi-stream). */
    incomeStreamPayorKey?: string | null;
    summaryLine: string;
  }>;
  /** ACTIVE non-FAIL streams included in normalized monthly income (when audit persisted streamIntelligence). */
  incomeStreams?: IncomeDashboardIncomeStream[];
  transactionMeta: {
    bankTransactionsReturned: number;
    bankTransactionsCapped: boolean;
    incomeDepositsReturned: number;
    incomeDepositsCapped: boolean;
  };
  /**
   * BankIncomeAudit.calculationTransactions — IQR / magnitude / monthly normalize
   * snapshot for admin explainability (null when absent or paystub-only shape without streams).
   */
  calculationTransactions?: Record<string, unknown> | null;
  gateFlowSteps: IncomeDashboardSummary['gateFlowSteps'];
  pipelineStatusLegend: IncomeDashboardSummary['pipelineStatusLegend'];
  finalDecisionLegend: IncomeDashboardSummary['finalDecisionLegend'];
};

export const incomeVerificationDashboardApi = {
  summary() {
    return incomeVerificationDashboardHttp
      .get<IncomeDashboardSummary>(`${BASE}/summary`)
      .then((r) => r.data);
  },

  list(params: {
    limit?: number;
    offset?: number;
    latestPerApplicationOnly?: boolean;
    finalStatus?: string;
    applicationIdContains?: string;
    ccidContains?: string;
    verifiedFrom?: string;
    verifiedTo?: string;
    sortBy?: string;
  }) {
    return incomeVerificationDashboardHttp
      .get<IncomeDashboardListResponse>(`${BASE}/records`, { params })
      .then((r) => r.data);
  },

  detail(id: string) {
    return incomeVerificationDashboardHttp
      .get<IncomeDashboardDetail>(`${BASE}/records/${encodeURIComponent(id)}`)
      .then((r) => r.data);
  },
};
